import React, { useCallback, useState, createContext, useContext } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
type ToastType = 'success' | 'error' | 'info' | 'warning';
interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
}
interface ToastContextType {
  showToast: (type: ToastType, title: string, message?: string) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = useCallback((type: ToastType, title: string, message?: string) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const newToast: Toast = {
      id,
      type,
      title,
      message
    };
    setToasts(prev => [...prev, newToast]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" data-id="element-1014" />;
      case 'error':
        return <AlertCircle className="w-5 h-5" data-id="element-1015" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" data-id="element-1016" />;
      case 'info':
        return <Info className="w-5 h-5" data-id="element-1017" />;
    }
  };
  const getStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-white dark:bg-gray-900 border-green-200 dark:border-green-800';
      case 'error':
        return 'bg-white dark:bg-gray-900 border-red-200 dark:border-red-800';
      case 'warning':
        return 'bg-white dark:bg-gray-900 border-yellow-200 dark:border-yellow-800';
      case 'info':
        return 'bg-white dark:bg-gray-900 border-blue-200 dark:border-blue-800';
    }
  };
  const getIconColor = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'info':
        return 'text-blue-600 dark:text-blue-400';
    }
  };
  return <ToastContext.Provider value={{
    showToast
  }} data-id="element-1018">
      {children}

      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 max-w-md pointer-events-none" data-id="element-1019">
        {toasts.map(toast => <div key={toast.id} className={`${getStyles(toast.type)} border-2 rounded-2xl shadow-soft-xl p-4 flex items-start gap-3 animate-slide-in pointer-events-auto`} data-id="element-1020">
            <div className={`${getIconColor(toast.type)} shrink-0 mt-0.5`} data-id="element-1021">
              {getIcon(toast.type)}
            </div>
            <div className="flex-1 min-w-0" data-id="element-1022">
              <p className="font-semibold text-sm text-gray-900 dark:text-white" data-id="element-1023">
                {toast.title}
              </p>
              {toast.message && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-1024">
                  {toast.message}
                </p>}
            </div>
            <button onClick={() => removeToast(toast.id)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors shrink-0" data-id="element-1025">
              <X size={16} data-id="element-1026" />
            </button>
          </div>)}
      </div>
    </ToastContext.Provider>;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}